"use client";

import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules, createValidation } from "../components/FormValidation";
import { cn } from "@/lib/utils";
import parse from "html-react-parser";

// Configuration interface for input fields
export interface InputFieldConfig {
  fieldName: string;
  label: string;
  placeholder?: string;
  isOptional?: boolean;
  inputType?: 'text' | 'number' | 'email' | 'tel' | 'url' | 'password';
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  helperText?: string;
  errorText?: string;
  showWhen?: {
    field: string;
    value: string | string[];
    operator?: 'equals' | 'notEquals' | 'in' | 'notIn';
  };
}

export interface MultiInputStepConfig {
  title?: string;
  description?: string;
  inputFields: InputFieldConfig[];
}

type MultiInputStepProps<T = BaseFormData> = StepProps<T>;

export const MultiInputStep = <T extends BaseFormData = BaseFormData>(props: MultiInputStepProps<T>) => {
  const { 
    formData, 
    title, 
    description
  } = props;
  
  const { control } = useFormContext();

  // Extract stepConfig with type safety
  const stepConfig = props.stepConfig as MultiInputStepConfig | undefined;

  const config = stepConfig || {
    title: "Enter your details",
    description: undefined,
    inputFields: []
  };

  const shouldShowField = (field: InputFieldConfig): boolean => {
    if (!field.showWhen) return true;
    const { field: conditionField, value: conditionValue, operator = 'equals' } = field.showWhen;
    const fieldValue = formData[conditionField as keyof T] as string;
    switch (operator) {
      case 'equals':
        return fieldValue === conditionValue;
      case 'notEquals':
        return fieldValue !== conditionValue;
      case 'in':
        return Array.isArray(conditionValue) && conditionValue.includes(fieldValue);
      case 'notIn':
        return Array.isArray(conditionValue) && !conditionValue.includes(fieldValue);
      default:
        return true;
    }
  };

  const getRulesForField = (field: InputFieldConfig) => {
    const builder = createValidation();
    
    // Required validation
    if (!field.isOptional && shouldShowField(field)) {
      builder.required(field.label);
    }
    
    // Length validations
    if (field.minLength) {
      builder.minLength(field.label, field.minLength);
    }
    if (field.maxLength) {
      builder.maxLength(field.label, field.maxLength);
    }
    
    // Input type specific validations
    if (field.inputType === 'email' || /email/i.test(field.fieldName)) {
      builder.email(field.label);
    } else if (field.inputType === 'tel' || /phone/i.test(field.fieldName)) {
      builder.phone(field.label);
    } else if (field.inputType === 'number') {
      builder.custom(ValidationRules.positiveInteger());
    }
    
    // Custom pattern validation
    if (field.pattern) {
      try {
        const regex = new RegExp(field.pattern);
        builder.custom(ValidationRules.customPattern(field.label, regex, field.errorText));
      } catch (error) {
        console.warn(error);
        console.warn(`Invalid regex pattern for field ${field.fieldName}:`, field.pattern);
      }
    }
    
    return builder.build();
  };

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-4">
        <h3>{title}</h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">{parse(description)}</p>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {config.title && (
          <h3 className="paragraph-base text-foreground font-bold">{config.title}</h3>
        )}
        {config.description && (
          <p className="paragraph-base text-muted-foreground">{parse(config.description)}</p>
        )}
        
        <div className="flex flex-col gap-10">
          {config.inputFields.map((field: InputFieldConfig) => {
            if (!shouldShowField(field)) return null;
            const rules = getRulesForField(field);
            return (
              <div key={field.fieldName} className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={field.fieldName as never}
                  rules={rules}
                  render={({ field: rhfField }) => (
                    <FormItem>
                      <FormLabel className="paragraph-base font-bold ">
                        {field.label}
                        {field.isOptional && (
                          <span className="paragraph-sm text-muted-foreground font-normal">
                            (Optional)
                          </span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          id={field.fieldName}
                          type={field.inputType || 'text'}
                          {...rhfField}
                          value={(rhfField.value as string) || ""}
                          placeholder={field.placeholder || `Enter your ${field.label.toLowerCase()}`}
                          minLength={field.minLength}
                          maxLength={field.maxLength}
                          className={cn("w-full")}
                        />
                      </FormControl>
                      {field.helperText && (
                        <p className="paragraph-sm text-muted-foreground">{parse(field.helperText)}</p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};
